/**
 * Created by Claude on 26.01.2026
 *
 * Arkadium SDK Wallet/Payments integration
 * Uses Arkadium's gem-based virtual currency system
 * https://arkadiumsdk.z19.web.core.windows.net/docs/features/wallet
 * https://arkadiumsdk.z19.web.core.windows.net/docs/features/host
 */

const ArkadiumPayments = function () {
    Payments.call(this, connector.ARKADIUM, {
        sdk: SdkManager.SDK_ARKADIUM
    });

    this.purchases = [];
    this.consumeQueue = {};

    this.load();

    this.processConsumeQueueThrottled = connector.utils.timeredThrottle(10000, () => this.processConsumeQueue());

    connector.timeouts.setInterval(() => this.processConsumeQueueThrottled(), connector.utils.parseInterval("2 minutes"));
};

ArkadiumPayments.prototype = Object.create(Payments.prototype);
ArkadiumPayments.prototype.constructor = ArkadiumPayments;

ArkadiumPayments.prototype.getDefaultCurrency = function () {
    return "ARKADIUM_GEMS";
};

ArkadiumPayments.prototype.isVirtualCurrency = function () {
    return true;
};

ArkadiumPayments.prototype.getTextPrice = function (price) {
    return `${connector.utils.formatAmount(price)} GEMS`;
};

ArkadiumPayments.prototype.getProductId = function (config) {
    return config.id.arkadium;
};

ArkadiumPayments.prototype.load = function () {
    const info = connector.dataLoader.load(SimpleDataLoader.TYPES.ARKADIUM_PAYMENTS) || {};

    this.purchases = info.purchases || [];
    this.consumeQueue = info.consumeQueue || {};
};

ArkadiumPayments.prototype.save = function () {
    connector.dataLoader.save(SimpleDataLoader.TYPES.ARKADIUM_PAYMENTS, {
        purchases: this.purchases,
        consumeQueue: this.consumeQueue
    });
};

ArkadiumPayments.prototype._connect = function (callback) {
    ArkadiumSDK.wallet.isGemsSupported()
        .then((isSupported) => {
            console.log(`payments isSupported ${isSupported}`);
            callback(isSupported ? connector.STATUS_CONNECTED : connector.STATUS_DISABLED);
        })
        .catch((error) => {
            console.log("payments connect error", error);
            callback(connector.STATUS_DISCONNECTED);
        });
};

ArkadiumPayments.prototype.purchase = function (product, callback) {
    if (!product.contents || product.contents.length === 0) {
        callback(ERRORS.PURCHASE.UNKNOWN);
        return;
    }

    const sku = product.productId;
    const price = this.getPriceToBePaid(product);

    this.getBalance((code, balance) => {
        if (code !== connector.CODE_SUCCEED) {
            callback(ERRORS.PURCHASE.UNKNOWN);
            return;
        }

        if (balance < price) {
            this.openPurchaseForm();
            callback(ERRORS.PURCHASE.INSUFFICIENT_FUNDS);
            return;
        }

        ArkadiumSDK.wallet.purchaseBundle(sku, 1)
            .then((success) => {
                console.log(`payments purchaseBundle ${sku} success ${success}`);

                if (!success) {
                    callback(ERRORS.PURCHASE.UNKNOWN);
                    return;
                }

                const purchase = this.createPurchase(Date.now(), product);
                this.savePurchase(purchase);

                callback(connector.CODE_SUCCEED, purchase);
            })
            .catch((error) => {
                console.log("payments purchaseBundle error", error);
                callback(ERRORS.PURCHASE.UNKNOWN, undefined, { error: error && error.code, message: error && error.message });
            });
    });
};

ArkadiumPayments.prototype.getBalance = function (callback) {
    ArkadiumSDK.wallet.getGems()
        .then((gemsAmount) => {
            gemsAmount = gemsAmount || 0;
            console.log(`payments gemsAmount ${gemsAmount}`);
            callback(connector.CODE_SUCCEED, gemsAmount);
        })
        .catch((error) => {
            console.log("payments gemsAmount error", error);
            callback(connector.CODE_FAILED, 0);
        });
};

ArkadiumPayments.prototype._loadProducts = function (callback) {
    ArkadiumSDK.wallet.getBundles()
        .then((bundles) => {
            bundles = bundles || {};

            const loadedIds = [];

            Object.keys(bundles).forEach((sku) => {
                const product = this.getProduct(sku);
                if (!product) {
                    return;
                }

                const bundle = bundles[sku];

                product.contents = bundle.contents || [];
                product.price = bundle.price;
                product.textPrice = this.getTextPrice(bundle.price);

                loadedIds.push(product.productId);
            });

            callback(connector.CODE_SUCCEED, loadedIds);
        })
        .catch((error) => {
            console.log("products load error", error);
            callback(connector.CODE_FAILED);
        });
};

ArkadiumPayments.prototype.isRestoreAvailable = function () {
    return this.productsLoaded;
};

ArkadiumPayments.prototype.restore = function (callback) {
    const owned = this.purchases.find((purchase) => !purchase.consumed);
    if (owned) {
        callback(connector.CODE_SUCCEED, { ...owned });
        return;
    }

    ArkadiumSDK.wallet.getInventory()
        .then((inventory) => {
            inventory = inventory || {};

            const amounts = {};

            for (const sku in inventory) {
                const amount = (inventory[sku].amount || 0) - (this.consumeQueue[sku] || 0);
                if (amount > 0) {
                    amounts[sku] = amount;
                }
            }
            if (Object.keys(amounts).length === 0) {
                callback(connector.CODE_FAILED);
                return;
            }

            const products = this.listProducts().filter((product) => product.contents && product.contents.length > 0);

            let pending = products.sort((a, b) => b.price - a.price)
                .find((product) => product.contents.every((item) => item.amount && amounts[item.sku] >= item.amount));

            if (!pending) {
                products.sort((a, b) => a.price - b.price);

                for (const sku in amounts) {
                    const cheapest = products.find((product) => product.contents.find((item) => item.sku === sku && item.amount >= amounts[sku]));

                    if (cheapest) {
                        pending = cheapest;
                        break;
                    }
                }
            }

            if (!pending) {
                callback(connector.CODE_FAILED);
                return;
            }

            const purchase = this.createPurchase(Date.now(), pending);
            this.savePurchase(purchase);
            callback(connector.CODE_SUCCEED, purchase);
        })
        .catch((error) => {
            console.log("payments getInventory error", error);
            callback(connector.CODE_FAILED);
        });
};

ArkadiumPayments.prototype.consume = function (purchase, callback) {
    const saved = this.purchases.find((saved) => saved.paymentId === purchase.paymentId);
    if (saved) {
        saved.consumed = true;
    }

    const product = this.getProduct(purchase.productId);
    const contents = (product && product.contents) || [];

    contents.forEach((item) => {
        this.consumeQueue[item.sku] = (this.consumeQueue[item.sku] || 0) + item.amount;
    });

    this.save();

    this.processConsumeQueueThrottled();

    ["20 seconds", "40 seconds", "60 seconds"].forEach((timeout) => {
        connector.timeouts.setTimeout(() => this.processConsumeQueueThrottled(), connector.utils.parseInterval(timeout));
    });

    callback(connector.CODE_SUCCEED);
};

ArkadiumPayments.prototype.openPurchaseForm = function () {
    ArkadiumSDK.host.openPurchaseForm()
        .then(() => {
            console.log("payments openPurchaseForm success");
        })
        .catch((error) => {
            console.log("payments openPurchaseForm error", error);
        });
};

ArkadiumPayments.prototype.savePurchase = function (purchase) {
    this.purchases.push(purchase);

    if (this.purchases.length > ArkadiumPayments.PURCHASES_LIMIT) {
        this.purchases = this.purchases.slice(-ArkadiumPayments.PURCHASES_LIMIT);
    }

    this.save();
};

ArkadiumPayments.prototype.processConsumeQueue = function () {
    if (this.consumeQueueProcessing || !this.productsLoaded) {
        return;
    }

    let changed = false;

    for (const sku in this.consumeQueue) {
        if (this.consumeQueue[sku] <= 0) {
            delete this.consumeQueue[sku];
            changed = true;
        }
    }

    if (changed) {
        this.save();
    }

    if (Object.keys(this.consumeQueue).length === 0) {
        return;
    }

    this.consumeQueueProcessing = true;

    ArkadiumSDK.wallet.getInventory().then((inventory) => {
        inventory = inventory || {};

        return Promise.all(Object.keys(this.consumeQueue).map((sku) => {
            let amount = (inventory[sku] && inventory[sku].amount) || 0;

            if (amount <= 0) {
                delete this.consumeQueue[sku];
                this.save();
                return;
            }

            amount = Math.min(this.consumeQueue[sku], amount);

            return ArkadiumSDK.wallet.consumeInventoryItem(sku, amount)
                .then((success) => {
                    console.log(`payments consumeInventoryItem ${sku} x${amount} success ${success}`);
                    if (!success) {
                        return;
                    }

                    this.consumeQueue[sku] = Math.max(this.consumeQueue[sku] - amount, 0);
                    this.save();
                })
                .catch((error) => {
                    console.log("payments consumeInventoryItem error", sku, error);
                });
        }));
    }).then(() => {
        this.consumeQueueProcessing = false;
    }).catch((error) => {
        console.log("payments processConsumeQueue error", error);
        this.consumeQueueProcessing = false;
    });
};

ArkadiumPayments.isAppropriate = function () {
    return connector.platform.oneOf(connector.ARKADIUM);
};

ArkadiumPayments.PURCHASES_LIMIT = 30;